"use client";

import Image from "next/image";
import { type CSSProperties } from "react";

import { CcHeroAnnouncement } from "@/components/home/CcHeroAnnouncement";
import { CcHeroLogoBand } from "@/components/home/CcHeroLogoBand";
import { CcHeroServicesTicker } from "@/components/home/CcHeroServicesTicker";
import { CcButton } from "@/components/ui/CcButton";
import { CcEyebrow } from "@/components/ui/CcEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import {
  HERO_GALLERY_PETROL_BACKDROP_SRC,
  HOME_HERO_AVIF_SRC_SET,
  HOME_HERO_WEBP_SRC_SET,
  getHeroBackdropImage,
  getHeroNavDataAttrFromTone,
  getHeroTheme,
  heroNavToneFromOverlayTheme,
  type HeroLogoBandLayout,
  type HeroNavTone,
  type HeroTheme,
} from "@/lib/hero-theme";
import type {
  CcDevHeroBackdropBlendMode,
  CcDevHeroBackdropLayout,
} from "@/lib/dev/hero-options";
import type { HeroTickerItem } from "@/lib/services";
import { useCcHeroScrollParallax } from "@/lib/use-cc-hero-scroll-parallax";

type CcHeroAnnouncementProps = {
  label: string;
  text: string;
  href: string;
};

export type CcHeroAccentStyle = "brand" | "neutral";

type CcHeroProps = {
  className?: string;
  announcement?: CcHeroAnnouncementProps;
  eyebrow?: string;
  title: string;
  description: string;
  primaryCta: string;
  secondaryCta: string;
  primaryHref: string;
  secondaryHref: string;
  logoBandCaption?: string;
  /** Default compact matches production home; full = edge-to-edge marquee band. */
  logoBandLayout?: HeroLogoBandLayout;
  tickerItems?: HeroTickerItem[];
  /** Dev-only homepage hero token from cookie/query; ignored in production. */
  devHeroTheme?: HeroTheme;
  /** Shipped home overlay token; dev cookie/query overrides when set. */
  overlayTheme?: HeroTheme;
  /** Explicit navbar tone over the hero; dev overlay override recalculates when `devHeroTheme` is set. */
  heroNavTone?: HeroNavTone;
  /** Dev gallery: swap backdrop while keeping theme overlay tokens. */
  backdropSrc?: string;
  /** Dev gallery: alternate backdrop composition (default full-bleed). */
  backdropLayout?: CcDevHeroBackdropLayout;
  /** Dev gallery: blend opaque JPEG sky into `$cc-color-bg-hero` (corner-anchor). */
  backdropBlendMode?: CcDevHeroBackdropBlendMode;
  /** When true, skip fixed `id="hero"` (stacked dev previews). */
  suppressHeroId?: boolean;
  imagePriority?: boolean;
  /** Headline `<b>` color + primary CTA variant; production defaults to brand teal. */
  accentStyle?: CcHeroAccentStyle;
  /** Dev gallery: desaturate backdrop artwork (corner-anchor previews). */
  backdropGrayscale?: boolean;
  /** Dev gallery: nudge warm backdrop art toward `$cc-color-brand` (corner-anchor). */
  backdropTone?: "natural" | "brand-green";
  /** Dev lab: `--cc-hero-grain-opacity` (omit in production — SCSS default `0.1`). */
  devGrainOpacity?: number;
  /** Dev lab: `--cc-hero-backdrop-scrim-opacity` multiplier (omit in production — default `1`). */
  devBackdropScrimStrength?: number;
  /** Full-width homepage banner that recedes behind the next section on scroll. */
  scrollParallax?: boolean;
};

export function CcHero({
  className = "",
  announcement,
  eyebrow,
  title,
  description,
  primaryCta,
  secondaryCta,
  primaryHref,
  secondaryHref,
  logoBandCaption,
  logoBandLayout = "compact",
  tickerItems = [],
  devHeroTheme,
  overlayTheme,
  heroNavTone,
  backdropSrc,
  backdropLayout = "default",
  backdropBlendMode = "normal",
  suppressHeroId = false,
  imagePriority = true,
  accentStyle = "brand",
  backdropGrayscale = false,
  backdropTone = "natural",
  devGrainOpacity,
  devBackdropScrimStrength,
  scrollParallax = false,
}: CcHeroProps) {
  const sectionRef = useCcHeroScrollParallax(scrollParallax);

  const heroTheme =
    devHeroTheme ?? overlayTheme ?? getHeroTheme(devHeroTheme);
  const heroBackdropImage = backdropSrc ?? getHeroBackdropImage(devHeroTheme);
  const heroNavMode =
    devHeroTheme != null
      ? getHeroNavDataAttrFromTone(heroNavToneFromOverlayTheme(devHeroTheme))
      : getHeroNavDataAttrFromTone(
          heroNavTone ?? heroNavToneFromOverlayTheme(heroTheme),
        );
  // PNG alpha is flattened to black when Next/Image optimizes to WebP — serve PNG as-is.
  const backdropPreserveAlpha = heroBackdropImage.toLowerCase().endsWith(".png");
  const layoutClass =
    backdropLayout === "corner-anchor"
      ? " cc-hero--layout-corner-anchor"
      : backdropLayout === "centered"
        ? " cc-hero--layout-centered"
        : "";
  const eyebrowTheme =
    backdropLayout === "corner-anchor" && heroTheme === "dark" ? "on-subtle" : "on-dark";
  const backdropBlendClass =
    backdropLayout === "corner-anchor" && backdropBlendMode === "screen"
      ? " cc-hero__backdrop-image--blend-screen"
      : "";
  const backdropGrayscaleClass = backdropGrayscale
    ? " cc-hero__backdrop-image--grayscale"
    : "";
  const backdropToneClass =
    backdropLayout === "corner-anchor" && backdropTone === "brand-green"
      ? " cc-hero__backdrop-image--tone-brand"
      : "";
  const accentClass = accentStyle === "neutral" ? " cc-hero--accent-neutral" : "";
  const primaryButtonVariant = accentStyle === "neutral" ? "dark" : "primary";
  const isCenteredBackdrop = backdropLayout === "centered";
  const logoBand = (
    <CcHeroLogoBand caption={logoBandCaption} layout={logoBandLayout} />
  );
  const devBackdropVisualStyle: CSSProperties | undefined =
    devGrainOpacity != null || devBackdropScrimStrength != null
      ? ({
          ...(devGrainOpacity != null && {
            "--cc-hero-grain-opacity": String(devGrainOpacity),
          }),
          ...(devBackdropScrimStrength != null && {
            "--cc-hero-backdrop-scrim-opacity": String(devBackdropScrimStrength),
          }),
        } as CSSProperties)
      : undefined;

  return (
    <section
      ref={sectionRef}
      className={`${className} cc-hero cc-hero--home cc-hero--theme-${heroTheme}${layoutClass}${accentClass}${scrollParallax ? " cc-hero--scroll-parallax" : ""}`}
      id={suppressHeroId ? undefined : "hero"}
      data-cc-nav={heroNavMode}
      style={devBackdropVisualStyle}
      {...devSectionAttr("hero")}
    >
      <div className="cc-hero__frame">
        <div className="cc-hero__backdrop" aria-hidden="true">
          {heroBackdropImage === HERO_GALLERY_PETROL_BACKDROP_SRC ? (
            <picture>
              <source type="image/avif" srcSet={HOME_HERO_AVIF_SRC_SET} sizes="100vw" />
              <source type="image/webp" srcSet={HOME_HERO_WEBP_SRC_SET} sizes="100vw" />
              {/* Pre-encoded assets avoid a cold image-worker request for the LCP artwork. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/assets/imgs/header/home-hero/petrol-1024.webp" alt="" width={1024} height={729}
                loading={imagePriority ? "eager" : "lazy"} fetchPriority={imagePriority ? "high" : "auto"}
                className={`cc-hero__backdrop-image${backdropBlendClass}${backdropToneClass}${backdropGrayscaleClass}`}
                style={{position: "absolute", inset: 0, width: "100%", height: "100%"}} />
            </picture>
          ) : <Image
            src={heroBackdropImage}
            alt=""
            fill
            loading={imagePriority ? "eager" : "lazy"}
            fetchPriority={imagePriority ? "high" : "auto"}
            unoptimized={backdropPreserveAlpha}
            sizes="100vw"
            className={`cc-hero__backdrop-image${backdropBlendClass}${backdropToneClass}${backdropGrayscaleClass}`}
          />}
          <div className="cc-hero__bottom-glow" />
        </div>

        <div className="cc-hero__layout">
          <div className="cc-hero__content">
            {announcement ? (
              <CcHeroAnnouncement
                label={announcement.label}
                text={announcement.text}
                href={announcement.href}
              />
            ) : null}
            {eyebrow ? (
              <CcEyebrow
                as="p"
                theme={eyebrowTheme}
                iconName="Sparkles"
                className="cc-hero__eyebrow"
              >
                {eyebrow}
              </CcEyebrow>
            ) : null}
            <h1 className="cc-hero__title" dangerouslySetInnerHTML={{ __html: title }} />
            <p className="cc-hero__description">{description}</p>
            <div className="cc-hero__actions">
              <CcButton
                className="cc-hero__action cc-hero__action--primary"
                href={primaryHref}
                variant={primaryButtonVariant}
                expand
                expandCompact
                label={primaryCta}
              />
              <CcButton
                className="cc-hero__action cc-hero__action--secondary"
                href={secondaryHref}
                variant="outline"
                label={secondaryCta}
                hoverLabel={secondaryCta}
              />
            </div>
          </div>

          {!isCenteredBackdrop && tickerItems.length > 0 ? (
            <CcHeroServicesTicker items={tickerItems} />
          ) : null}
        </div>

        {logoBand}
      </div>
    </section>
  );
}
